import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ChevronLeft, ChevronRight, Plus, Loader2, Calendar as CalendarIcon,
    Trash2, Edit3, X, Filter,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { useAuth } from "@/context/AuthContext";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CONTENT_TYPES = { post: "Post", reel: "Reels", story: "Story", video: "Video", blog: "Blog Yazısı", ad: "Reklam", campaign: "Kampanya", other: "Diğer" };
const PLATFORMS = { instagram: "Instagram", facebook: "Facebook", twitter: "Twitter/X", linkedin: "LinkedIn", tiktok: "TikTok", youtube: "YouTube", website: "Web Sitesi", other: "Diğer" };
const STATUSES = { planned: "Planlandı", in_progress: "Hazırlanıyor", ready: "Hazır", scheduled: "Zamanlandı", published: "Yayınlandı", cancelled: "İptal" };
const STATUS_DOT = {
    planned: "bg-[hsl(var(--muted-foreground))]",
    in_progress: "bg-[hsl(var(--warning))]",
    ready: "bg-[hsl(var(--info))]",
    scheduled: "bg-[hsl(var(--brand))]",
    published: "bg-[hsl(var(--success))]",
    cancelled: "bg-[hsl(var(--destructive))]",
};

const MONTH_NAMES = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const DAY_NAMES = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";

// helpers
const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => (s ? new Date(s) : null);
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

// build month grid (Mon-Sun)
function buildMonth(year, month) {
    const first = new Date(year, month, 1);
    const firstWeekday = (first.getDay() + 6) % 7; // 0 = Monday
    const days = [];
    // previous month tail
    const prevMonthLast = new Date(year, month, 0).getDate();
    for (let i = firstWeekday - 1; i >= 0; i--) {
        days.push({ date: new Date(year, month - 1, prevMonthLast - i), inMonth: false });
    }
    const lastDay = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= lastDay; d++) days.push({ date: new Date(year, month, d), inMonth: true });
    while (days.length < 42) {
        const last = days[days.length - 1].date;
        days.push({ date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1), inMonth: false });
    }
    return days;
}

// ------------- Event Modal -------------
function EventModal({ open, onClose, onSaved, initial, customers, users, canWrite }) {
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState({
        title: "", description: "", customer_id: "", content_type: "post",
        platform: "instagram", scheduled_at: "", duration_minutes: 30,
        assignee_id: "", status: "planned", notes: "",
    });

    useEffect(() => {
        if (!open) return;
        if (initial?.id) {
            setForm({
                title: initial.title || "", description: initial.description || "",
                customer_id: initial.customer_id || "", content_type: initial.content_type || "post",
                platform: initial.platform || "instagram",
                scheduled_at: initial.scheduled_at ? new Date(initial.scheduled_at).toISOString().slice(0, 16) : "",
                duration_minutes: initial.duration_minutes || 30,
                assignee_id: initial.assignee_id || "",
                status: initial.status || "planned",
                notes: initial.notes || "",
            });
        } else {
            const preset = initial?.presetDate ? initial.presetDate : new Date();
            const iso = new Date(preset.getFullYear(), preset.getMonth(), preset.getDate(), 10, 0).toISOString().slice(0, 16);
            setForm({
                title: "", description: "", customer_id: "", content_type: "post",
                platform: "instagram", scheduled_at: iso, duration_minutes: 30,
                assignee_id: "", status: "planned", notes: "",
            });
        }
    }, [open, initial]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return toast.error("Başlık zorunlu.");
        if (!form.scheduled_at) return toast.error("Tarih/saat zorunlu.");
        setSaving(true);
        try {
            const payload = { ...form };
            payload.scheduled_at = new Date(form.scheduled_at).toISOString();
            payload.duration_minutes = Number(form.duration_minutes) || 30;
            if (!payload.customer_id) delete payload.customer_id;
            if (!payload.assignee_id) delete payload.assignee_id;
            const { data } = isEdit
                ? await api.put(`/content-calendar/${initial.id}`, payload)
                : await api.post("/content-calendar", payload);
            toast.success(isEdit ? "Etkinlik güncellendi." : "Etkinlik eklendi.");
            onSaved && onSaved(data);
            onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi."); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl">
                <DialogHeader><DialogTitle>{isEdit ? "Etkinliği Düzenle" : "Yeni İçerik"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <input required placeholder="Başlık (örn. Instagram Reels)" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" disabled={!canWrite} />
                    <div className="grid grid-cols-2 gap-2">
                        <select className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} disabled={!canWrite} data-testid="event-customer-select">
                            <option value="">— Müşteri —</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select className={inputCls} value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} disabled={!canWrite}>
                            <option value="">— Sorumlu —</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <select className={inputCls} value={form.content_type} onChange={(e) => setForm({ ...form, content_type: e.target.value })} disabled={!canWrite}>
                            {Object.entries(CONTENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <select className={inputCls} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} disabled={!canWrite}>
                            {Object.entries(PLATFORMS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[11px] text-muted-foreground mb-1">Tarih & Saat</label>
                            <input required type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} disabled={!canWrite} data-testid="event-datetime-input" />
                        </div>
                        <div>
                            <label className="block text-[11px] text-muted-foreground mb-1">Süre (dk)</label>
                            <input type="number" min="5" step="5" className={inputCls} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} disabled={!canWrite} />
                        </div>
                    </div>
                    <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <textarea rows={3} placeholder="Açıklama" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={!canWrite} />
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                        <button type="submit" disabled={saving || !canWrite} className="btn-brand" data-testid="event-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isEdit ? "Güncelle" : "Kaydet"}</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ------------- Main -------------
export default function ContentCalendarPage() {
    const { user } = useAuth();
    const [cursor, setCursor] = useState(() => new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [canWrite, setCanWrite] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [filterCustomer, setFilterCustomer] = useState("");
    const [filterPlatform, setFilterPlatform] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [selectedDay, setSelectedDay] = useState(toYMD(new Date()));
    const [modalOpen, setModalOpen] = useState(false);
    const [modalInitial, setModalInitial] = useState(null);

    const monthGrid = useMemo(() => buildMonth(cursor.getFullYear(), cursor.getMonth()), [cursor]);
    const monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const start = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1).toISOString();
            const end = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 0, 23, 59, 59).toISOString();
            const params = { start, end, page_size: 1000 };
            if (filterCustomer) params.customer_id = filterCustomer;
            if (filterPlatform) params.platform = filterPlatform;
            if (filterStatus) params.status = filterStatus;
            const { data } = await api.get("/content-calendar", { params });
            setEvents(data.items || []);
            setCanWrite(!!data.can_write);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [cursor, filterCustomer, filterPlatform, filterStatus]);

    useEffect(() => { load(); }, [load]);

    // Load customer & user lookups once
    useEffect(() => {
        (async () => {
            try {
                const [c, u] = await Promise.all([
                    api.get("/customers", { params: { page_size: 200 } }),
                    api.get("/employees", { params: { page_size: 200 } }).catch(() => ({ data: { items: [] } })),
                ]);
                setCustomers((c.data.items || []).map((x) => ({ id: x.id, name: x.name })));
                setUsers((u.data.items || []).filter((x) => x.role !== "client").map((x) => ({ id: x.id, full_name: x.full_name })));
            } catch { /* silent */ }
        })();
    }, []);

    const byDay = useMemo(() => {
        const map = {};
        for (const e of events) {
            if (!e.scheduled_at) continue;
            const d = new Date(e.scheduled_at);
            const key = toYMD(d);
            (map[key] = map[key] || []).push(e);
        }
        for (const k of Object.keys(map)) map[k].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
        return map;
    }, [events]);

    const dayEvents = byDay[selectedDay] || [];

    const openNew = (dateObj) => {
        setModalInitial({ presetDate: dateObj });
        setModalOpen(true);
    };
    const openEdit = (evt) => {
        setModalInitial(evt);
        setModalOpen(true);
    };
    const onSaved = (evt) => {
        setEvents((prev) => {
            const idx = prev.findIndex((x) => x.id === evt.id);
            if (idx >= 0) { const c = [...prev]; c[idx] = evt; return c; }
            return [...prev, evt];
        });
        setSelectedDay(toYMD(new Date(evt.scheduled_at)));
    };
    const del = async (id) => {
        if (!window.confirm("Bu etkinlik silinsin mi?")) return;
        try { await api.delete(`/content-calendar/${id}`); setEvents((prev) => prev.filter((x) => x.id !== id)); toast.success("Silindi."); }
        catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi."); }
    };

    const goMonth = (delta) => {
        const c = new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1);
        setCursor(c);
    };
    const goToday = () => { setCursor(new Date()); setSelectedDay(toYMD(new Date())); };

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight" data-testid="calendar-title">Detaylı İçerik Takvimi</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">Firma bazlı filtreleme, saat ve içerik tipi ile interaktif takvim.</p>
                </div>
                {canWrite && (
                    <button onClick={() => openNew(new Date(selectedDay))} className="btn-brand h-11" data-testid="btn-new-event">
                        <Plus className="h-4 w-4" /> Yeni İçerik
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="card-elevated p-3 flex flex-wrap gap-2 items-center">
                <div className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground px-1"><Filter className="h-3.5 w-3.5" /> Filtre</div>
                <select className={`${inputCls} max-w-[220px]`} value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)} data-testid="filter-customer">
                    <option value="">Tüm Müşteriler</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select className={`${inputCls} max-w-[160px]`} value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}>
                    <option value="">Tüm Platformlar</option>
                    {Object.entries(PLATFORMS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select className={`${inputCls} max-w-[160px]`} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {(filterCustomer || filterPlatform || filterStatus) && (
                    <button onClick={() => { setFilterCustomer(""); setFilterPlatform(""); setFilterStatus(""); }} className="text-[12px] text-[hsl(var(--brand))] hover:underline inline-flex items-center gap-1">
                        <X className="h-3.5 w-3.5" /> Temizle
                    </button>
                )}
            </div>

            {/* Month controls */}
            <div className="card-elevated p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button onClick={() => goMonth(-1)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted"><ChevronLeft className="h-4 w-4" /></button>
                    <div className="text-[15px] font-bold min-w-[160px] text-center" data-testid="calendar-month-label">{monthLabel}</div>
                    <button onClick={() => goMonth(1)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted"><ChevronRight className="h-4 w-4" /></button>
                    <button onClick={goToday} className="ml-2 inline-flex h-9 items-center rounded-lg border border-border px-3 text-[12.5px] font-semibold hover:bg-muted">Bugün</button>
                </div>
                <div className="text-[12px] text-muted-foreground">
                    {loading ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Yükleniyor</span> : `${events.length} etkinlik`}
                </div>
            </div>

            {/* Calendar grid + selected day */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
                <div className="card-elevated overflow-hidden">
                    <div className="grid grid-cols-7 bg-muted/40 border-b border-border">
                        {DAY_NAMES.map((d) => (
                            <div key={d} className="px-2 py-2 text-[11px] font-bold uppercase text-muted-foreground text-center">{d}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-7">
                        {monthGrid.map(({ date, inMonth }, idx) => {
                            const key = toYMD(date);
                            const items = byDay[key] || [];
                            const isSelected = selectedDay === key;
                            const isToday = toYMD(new Date()) === key;
                            return (
                                <button
                                    key={idx}
                                    onClick={() => setSelectedDay(key)}
                                    onDoubleClick={() => canWrite && openNew(date)}
                                    className={`min-h-[92px] border-b border-r border-border px-2 py-1.5 text-left transition-colors ${inMonth ? "bg-background" : "bg-muted/30 text-muted-foreground"} ${isSelected ? "ring-2 ring-inset ring-[hsl(var(--brand))]/60" : "hover:bg-muted/40"}`}
                                    data-testid={`cal-day-${key}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className={`text-[12px] font-bold tabular-nums ${isToday ? "inline-flex h-6 w-6 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-white" : ""}`}>{date.getDate()}</span>
                                        {items.length > 0 && <span className="text-[10px] font-semibold text-muted-foreground">{items.length}</span>}
                                    </div>
                                    <div className="mt-1 space-y-0.5">
                                        {items.slice(0, 3).map((e) => (
                                            <div key={e.id} className="flex items-center gap-1.5 truncate text-[10.5px]">
                                                <span className={`inline-block h-1.5 w-1.5 rounded-full ${STATUS_DOT[e.status] || "bg-muted"}`}></span>
                                                <span className="truncate">{fmtTime(e.scheduled_at)} · {e.title}</span>
                                            </div>
                                        ))}
                                        {items.length > 3 && <div className="text-[10px] text-muted-foreground">+{items.length - 3} daha…</div>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Right panel — selected day */}
                <div className="card-elevated p-4 h-fit sticky top-4">
                    <div className="flex items-center gap-2 mb-3">
                        <CalendarIcon className="h-4 w-4 text-[hsl(var(--brand))]" />
                        <div className="text-[13.5px] font-bold" data-testid="selected-day-label">
                            {new Date(selectedDay).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                        </div>
                    </div>
                    {dayEvents.length === 0 ? (
                        <div className="text-[12.5px] text-muted-foreground py-6 text-center">
                            Bu gün için içerik yok.
                            {canWrite && (
                                <div className="mt-3">
                                    <button onClick={() => openNew(new Date(selectedDay))} className="text-[12.5px] font-semibold text-[hsl(var(--brand))] hover:underline">+ İçerik ekle</button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {dayEvents.map((e) => (
                                <li key={e.id} className="rounded-xl border border-border p-3 hover:bg-muted/40 transition-colors" data-testid={`day-event-${e.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT[e.status]}`}></span>
                                                <span className="text-[11.5px] font-semibold text-muted-foreground">{fmtTime(e.scheduled_at)} · {STATUSES[e.status] || e.status}</span>
                                            </div>
                                            <div className="text-[13.5px] font-bold mt-0.5 truncate">{e.title}</div>
                                            <div className="text-[11.5px] text-muted-foreground truncate">
                                                {e.customer_name || "—"} • {CONTENT_TYPES[e.content_type] || e.content_type} • {PLATFORMS[e.platform] || e.platform}
                                            </div>
                                            {e.assignee_name && <div className="text-[11px] text-muted-foreground mt-0.5">Sorumlu: {e.assignee_name}</div>}
                                            {e.description && <div className="text-[11.5px] text-muted-foreground mt-1 line-clamp-2">{e.description}</div>}
                                        </div>
                                        {canWrite && (
                                            <div className="flex flex-col gap-1">
                                                <button onClick={() => openEdit(e)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--brand))]" title="Düzenle" data-testid={`btn-edit-${e.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                                                <button onClick={() => del(e.id)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--destructive))]" title="Sil"><Trash2 className="h-3.5 w-3.5" /></button>
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <EventModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={onSaved} initial={modalInitial} customers={customers} users={users} canWrite={canWrite} />
        </div>
    );
}
